import { getComponentsManager } from 'piivo-poster-engine/src/lib/services/componentsManager';
import { getFontsManager } from 'piivo-poster-engine/src/lib/services/fontsManager';
import Vue from 'vue';

import i18n from '../../../core/i18n';
import services from '../../../core/services';
import { LanguagesManagerService } from '../../../core/services/languagesManagerService';
import { AttributeValues } from '../../common/api/attributes';
import { AttributeTypes, DisplayMode } from '../../common/constants';
import { getNormalizedValue, iterateForm } from '../../common/helpers/formsHelper';
import logs from '../api/logs';
import { PosterLog } from '../api/logsTypes';
import { TemplateForm, TemplateFormats, TemplateTypes, TemplateValues } from '../api/types';
import { DEFAULT_SIGNAGE_SOURCE, PosterMimeTypes, Statutes } from '../constants';
import { SearchAttributeMapping, SearchModeSearchConfiguration } from '../types/import';
import { CartPoster, FormatsCopiesMap, InputSummary } from '../types/poster';
import { getPosterService } from '.';
import { IAlertsService } from './../../../platform/services/types';
import { EditablePage, EditableTemplate } from './../types/templates';
import { TypeData, TypeFormData } from './templatesService';
import { ISignagesService, ITemplatesService, SearchResultUpdate } from './types';

let signageId = -1;

export class SignagesService implements ISignagesService {
  async getAttributeValues(alias: string) {
    const results: any = await Vue.http.get(
      `/api/signages/attributes/${alias}/values?lang=${LanguagesManagerService.getSelectedLocale()}&sorting=${encodeURIComponent(
        '[{"priority":"asc"},{"label":"asc"}]'
      )}`
    );

    return results.body;
  }

  /**
   * Gets signages synthesis.
   * @param storeAlias - Store alias
   * @param departmentsAlias - Departments alias
   */
  async getPendingSignagesSynthesis(storeAlias: string, departmentsAlias: string[]) {
    let request = `/api/signages/logs/fastsynthesis`;
    let didAddFirstParam = false;

    // Add store param
    if (storeAlias) {
      request += `${didAddFirstParam ? '&' : '?'}signages_signages_inputs_store=${storeAlias}`;
      didAddFirstParam = true;
    }

    // Add departments param
    if (departmentsAlias != null && departmentsAlias.length > 0) {
      request += `${didAddFirstParam ? '&' : '?'}departments=[${departmentsAlias.join(',')}]`;
      didAddFirstParam = true;
    }

    const results: any = await Vue.http.get(request);
    return results.body;
  }

  /**
   * Gets signages list for the options in parameter (Signages search).
   * @param options - Search options (paging, sorting, filtering options)
   */
  listSignages(options: Record<string, any>) {
    const parameters = [`lang=${LanguagesManagerService.getSelectedLocale()}`];
    if (options) {
      // Paging
      if (
        options.index !== null &&
        typeof options.index !== 'undefined' &&
        options.size !== null &&
        typeof options.size !== 'undefined'
      ) {
        parameters.push(
          `paging=${encodeURIComponent(`{"size":${options.size},"index":${options.index}}`)}`
        );
      }

      // Sort
      if (Array.isArray(options.sortBy)) {
        const sorts = [];
        for (const [sortKey, sortDirection] of options.sortBy) {
          if (sortKey && sortDirection !== 'none') {
            // Add sort options
            sorts.push(`{"${sortKey}":"${sortDirection}"}`);
          }
        }

        if (sorts.length > 0) {
          parameters.push(`sorting=${encodeURIComponent(`[${sorts.join(',')}]`)}`);
        }
      }

      // Filters
      if (options.filterBy) {
        const filters = [];
        for (const filterKey in options.filterBy) {
          if (options.filterBy.hasOwnProperty(filterKey)) {
            const filter = options.filterBy[filterKey];
            if (filter.value !== null) {
              if (filter.operator !== 'between') {
                if (Array.isArray(filter.value) && filter.value.length > 0) {
                  // Array of values
                  const orFilters = [];
                  for (const orFilter of filter.value) {
                    orFilters.push(
                      `{"operator":"${filter.operator}","attribute":"${filterKey}","value":"${orFilter.itemId}"}`
                    );
                  }
                  filters.push(`{"operator":"or","filters":[${orFilters.join(',')}]}`);
                } else if (
                  (typeof filter.value === 'string' && filter.value.length > 0) ||
                  typeof filter.value === 'boolean'
                ) {
                  // String or boolean value
                  filters.push(
                    `{"operator":"${filter.operator}","attribute":"${filterKey}","value":"${filter.value}"}`
                  );
                }
              } else if (Array.isArray(filter.value) && filter.value.length > 1) {
                // Between filter
                filters.push(
                  `{"operator":"${filter.operator}","attribute":"${filterKey}","values":["${filter.value[0]}","${filter.value[1]}"]}`
                );
              }
            }
          }
        }

        // Build 'and' request with all filters
        if (filters.length > 0) {
          parameters.push(
            `filtering=${encodeURIComponent(`{"operator":"and","filters":[${filters.join(',')}]}`)}`
          );
        }
      }
    }

    let request = '';
    if (parameters.length > 0) {
      request = `?${parameters.join('&')}`;
    }

    return Vue.http.get(`/api/signages/logs/search${request}`);
  }

  /**
   * Gets the normalized signage value so that it can be used
   * in the template and the form at the same time
   *
   * @param value - value object of the signage
   * @param allAttributes - all attributes of the signage
   */
  private getNormalizedSignageValue(
    value: AttributeValues.ValueObject,
    allAttributes: TemplateForm.Attribute[]
  ): AttributeValues.AttributeValue {
    let parsed = value.value;
    if (typeof parsed === 'string') {
      parsed = parsed.replace(/\\n/g, '\n');
    }
    // Format values of date type
    if (value.type === AttributeTypes.DATE) {
      parsed = Vue.filter('isoDateFull')(value.value);
    }

    // Always convert links to strings. AttributePanel can handle array of strings
    if (value.type === AttributeTypes.LINKS) {
      const attribute = allAttributes.find(attr => {
        if (value['attributeId']) {
          return attr.itemId === value['attributeId'];
        }
        return attr.alias === value.attributeAlias;
      });
      if (attribute) {
        switch (attribute.options.displayMode) {
          case DisplayMode.Table:
            const tableValue = (value.value || []) as AttributeValues.TableValue;
            parsed = tableValue.map(line =>
              line.map((colValue, colIndex) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const colAttribute =
                  attribute.options.displayOptions?.tableOptions?.columns[colIndex].attribute;
                if (!colAttribute) {
                  return null;
                }
                const colAttrValue: AttributeValues.ValueObject = {
                  attributeAlias: colAttribute.alias,
                  attributeId: colAttribute.itemId,
                  type: colAttribute.type,
                  value: colValue,
                };

                return this.getNormalizedSignageValue(colAttrValue, [colAttribute]);
              })
            );
            break;
          case DisplayMode.Tiles:
            break;
          case DisplayMode.Tree:
          case DisplayMode.Combo:
            if (typeof value.value === 'string') {
              parsed = value.value;
            }

            if (value.value && typeof value.value === 'object' && !Array.isArray(value.value)) {
              parsed = (value.value as AttributeValues.ComboValueItem).alias;
            }
            break;
        }
      }
    }

    return parsed;
  }

  /**
   * Loads signages by signage Ids.
   * @param signageIds - Signage Ids
   * @param forEdition - Flag to indicate if signage loading is for edition purpose
   */
  async loadSignages(signageIds: string[], forEdition: boolean): Promise<EditableTemplate[]> {
    const { body: posters } = await logs.getSignagesLogs(signageIds, forEdition);

    return this.convertPosterLogsToTemplate(posters);
  }

  async convertPosterLogsToTemplate(
    signages: PosterLog.PosterLog[] | PosterLog.EditablePosterLog[]
  ): Promise<EditableTemplate[]> {
    const templatesPromises = [];
    for (const signage of signages) {
      const editableSignage: Omit<EditableTemplate, 'computedTemplate'> = {
        // Signage content
        templateId: signage.templateId,
        fonts: signage.content?.fonts ?? [],
        // Page is missing 'id' because it will be generated later
        pages: (signage.content?.pages as EditablePage[]) ?? [],

        // Signage
        itemId: signage.itemId,
        type: signage.type,
        format: signage.format,
        copies: signage.copies,
        comment: signage.comment,
        deadlineDate: Vue.filter('isoDateFull')(new Date(signage.deadlineDate)),
        form: Object.hasOwnProperty.call(signage, 'form')
          ? (signage as PosterLog.EditablePosterLog).form
          : null,
        paperType: signage.paperType,
        isTainted: signage.isTainted,
        isArchived: signage.isArchived,
        mimeType: signage.mimeType,

        // Editing
        bindings: {},
        types: {},
      };

      if (editableSignage.form) {
        iterateForm(editableSignage.form, (attribute, isInput) => {
          // Legacy compat: add search onAttributeResult if missing
          getPosterService<ITemplatesService>('templates')?.initCompatSearchResultConfig(
            attribute,
            isInput
          );
        });
      }

      if (signage.values && editableSignage.form) {
        for (const value of signage.values) {
          const normalizedValue = this.getNormalizedSignageValue(
            value,
            editableSignage.form.allAttributes
          );

          if (value.attributeAlias !== null) {
            editableSignage.bindings[value.attributeAlias] = normalizedValue;
            editableSignage.types[value.attributeAlias] = value.type;
          }
        }
      }
      if (signage.mimeType === PosterMimeTypes.Poster) {
        templatesPromises.push(
          (getComponentsManager().parseTemplateComponents(
            editableSignage,
            editableSignage.bindings
          ) as unknown) as Promise<EditableTemplate>
        );
      } else {
        // Non "Poster" don't have 'computedTemplate'
        templatesPromises.push(editableSignage as EditableTemplate);
      }
    }

    const templates = await Promise.all(templatesPromises);

    try {
      await getFontsManager().registerAllTemplatesFontsIfEnabled(templates);
    } catch (error) {
      // eslint-disable-next-line no-throw-literal
      throw { fontsError: true, error };
    }

    return templates;
  }

  /**
   * Gets signage details with a signage Id.
   *
   * @param signageId - Signage Id
   */
  async getSignageDetails(signageId: string) {
    const currentLanguage = LanguagesManagerService.getSelectedLocale();

    try {
      const res: any = await logs.getLogDetails(signageId, currentLanguage);
      return res.body;
    } catch (error) {
      // Alert error
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('poster.signage.error.retrieve_info') as string);

      throw error;
    }
  }

  /**
   * Deletes all the signages in parameter.
   *
   * @param signages - List of signages to delete
   */
  async deleteSignages(signages: { itemId: string }[]) {
    const toDelete = signages.map(signage => signage.itemId);

    try {
      const res: any = await logs.deleteLogs(toDelete);
      return res;
    } catch (err) {
      // Alert error
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('poster.signage.error.deletion') as string);

      throw err;
    }
  }

  /**
   * Archives all the signages in parameter.
   *
   * @param signages - List of signages to archive
   */
  async archiveSignages(signages: { itemId: string }[]) {
    const toArchive = signages.map(signage => signage.itemId);

    try {
      const res: any = await logs.archiveLogs(toArchive);
      return res;
    } catch (err) {
      // Alert error
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('poster.signage.error.archival') as string);

      throw err;
    }
  }

  /**
   * Updates signages status.
   *
   * @param signageIds - List of signage ids to update
   * @param newStatus - Status alias
   * @param parameters - update parameters
   */
  async changeStatuses(
    signageIds: string[],
    newStatus: string,
    parameters: PosterLog.CreationParameters | null
  ) {
    const updates = signageIds.map(itemId => ({ itemId, status: newStatus }));

    return logs.patchLogs(updates, parameters);
  }

  /**
   * Get logs table definition.
   */
  async getLogsTableDefinition() {
    try {
      const res: any = await logs.getLogsTableDefinition();
      return res.body;
    } catch (err) {
      // Alert error
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('poster.signage.error.retrieve_history_config') as string);

      throw err;
    }
  }

  /**
   * Get logs filters definition.
   */
  async getLogsFiltersDefinition() {
    try {
      const res: any = await logs.getLogsFiltersDefinition();
      return res.body;
    } catch (err) {
      // Alert error
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('poster.signage.error.retrieve_filters') as string);

      throw err;
    }
  }

  /**
   * Get logs details definition.
   */
  async getLogsDetailsDefinition() {
    try {
      const res: any = await logs.getLogsDetailsDefinition();
      return res.body;
    } catch (err) {
      // Alert error
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('poster.signage.error.retrieve_data') as string);

      throw err;
    }
  }

  /**
   * Checks if the signage has been modified.
   * @param {Object} form - Form object
   * @param taintedValues - signage tainted values
   */
  isSignageTainted(form: TemplateForm.Form, taintedValues: { [alias: string]: boolean }): boolean {
    let isTainted = false;
    if (form && form.attributeGroups) {
      for (const group of form.attributeGroups) {
        if (group.attributes) {
          for (const attribute of group.attributes) {
            if (taintedValues[attribute.alias] === true) {
              isTainted = true;
              break;
            }
          }
        }
      }
    }

    return isTainted;
  }

  /**
   * Creates a signage.
   * @param signage - the data poster
   * @param thumb - Thumbnail (image)
   * @param values - poster values
   * @param status - Signage status alias (status to initialize)
   * @param parameters - update parameters
   */
  updateSignage(
    signage: any,
    thumb: any,
    values: any,
    status = null,
    parameters: PosterLog.CreationParameters | null
  ) {
    const currentLanguage = LanguagesManagerService.getSelectedLocale();
    const signageRequest = {
      itemId: signage.itemId,
      status: status != null ? status : Statutes.TO_BE_PRINTED,
      label: { [currentLanguage]: values['designation'] },
      copies: signage.copies,
      comment: signage.comment,
      deadlineDate: signage.deadlineDate,
      thumbnail: thumb,
      isTainted: signage.isTainted,
      isArchived: signage.isArchived,
      values: [] as unknown[],
      source: DEFAULT_SIGNAGE_SOURCE,
      renderContents: signage.renderContents,
    };

    // Add signage values
    for (const key in values) {
      if (values.hasOwnProperty(key)) {
        signageRequest.values.push({
          attributeAlias: key,
          value: values[key],
        });
      }
    }

    return logs.patchLogs([signageRequest], parameters);
  }

  /**
   * Build signage object from parameters.
   * @param {Object} signageItem - Signage item
   * @param {Object} typeData - Type object with data
   * @param formatId - Format identifier (Guid)
   * @param status - Status alias
   * @param currentLanguage - Language code
   * @returns {Object} the signage object to save
   */
  buildSignage(
    signageItem: any,
    typeData: TypeData,
    formatId: string,
    status: string,
    currentLanguage: string
  ) {
    // Create signage object
    const template = typeData.templates.get(formatId);
    const signage = {
      status: status != null ? status : Statutes.TO_BE_PRINTED,
      templateId: template ? template.templateId : null,
      copies: signageItem.formatsCopiesMap[formatId].copies,
      comment: signageItem.comment,
      deadlineDate: signageItem.deadlineDate,
      correlationId: `${signageItem.id}-${formatId}`,
      thumbnail: null,
      isTainted: this.isSignageTainted(typeData.form!, signageItem.taintedValues),
      values: [] as unknown[],
      source: DEFAULT_SIGNAGE_SOURCE,
    };

    // Update label (if key defined for the type)
    const designationAlias = 'designation';
    (signage as any).label = typeData.valuesKeys.includes(designationAlias)
      ? { [currentLanguage]: signageItem.values[designationAlias] }
      : null;

    // Update signage values (only values defined for the form type)
    for (const key in signageItem.values) {
      if (signageItem.values.hasOwnProperty(key) && typeData.valuesKeys.includes(key)) {
        signage.values.push({ attributeAlias: key, value: signageItem.values[key] });
      }
    }

    return signage;
  }

  /**
   * Creates all signages
   *
   * @param posters - List of posters to create
   */
  async createSignages(
    posters: PosterLog.CreationLog[],
    parameters: PosterLog.CreationParameters | null
  ): Promise<PosterLog.PosterLog> {
    return logs.createPosters(posters, parameters);
  }

  /**
   * Build / Init new signage item with parameters.
   * @param signageId - signage identifier (counter)
   * @param familyId - Family identifier (Guid)
   * @param userExtendedProperties - User extended properties
   */
  buildnewSignageItem(
    signageId: number,
    familyId: string,
    userExtendedProperties: Record<string, any>
  ) {
    return {
      id: signageId,
      familyId: familyId,
      formatsCopiesMap: {},
      values:
        userExtendedProperties != null ? JSON.parse(JSON.stringify(userExtendedProperties)) : {},
      taintedValues: {},
      deadlineDate: Vue.filter('isoDateFull')(new Date()),
      comment: null,
      inputsSummary: [],
      areAttributesValid: true,
      isFormatsCopiesValid: true,
      isValid: true,
      nbDemands: 0,
      unread: true,
    };
  }

  /**
   * Initializes all defaults values, the formatCopies object,
   * resets the formatId if invalid, updates the inputs summary
   *
   * @param {Object} signageItem - Signage item to update
   * @param {Object} typeData - Type object with data (form etc.)
   * @param {Function} setValueFunction - Set signage item value function
   */
  updateSignageItemForType(
    signageItem: {
      values: { [alias: string]: unknown };
      taintedValues: { [alias: string]: boolean };
      type: TemplateTypes.RootType | TemplateTypes.Type;
      formatId: string | undefined | null;
      formatsCopiesMap: FormatsCopiesMap;
      inputsSummary: InputSummary[];
    },
    typeData: TypeData,
    setValueFunction?: (
      signageItem: any,
      alias: string,
      value: any,
      attribute: TemplateForm.Attribute | null
    ) => void
  ) {
    // Init type
    signageItem.type = typeData.type;

    // Init values with default values
    for (const alias in typeData.defaultValues) {
      if (
        setValueFunction != null &&
        typeData.defaultValues.hasOwnProperty(alias) &&
        typeof signageItem.values[alias] === 'undefined'
      ) {
        // Clone the value, so different posters won't reference the same
        // value
        const defaultValue = JSON.parse(JSON.stringify(typeData.defaultValues[alias]));

        const attribute = typeData.attributeMap.get(alias) ?? null;

        setValueFunction(signageItem, alias, defaultValue, attribute);
      }
    }

    // Init formats copies
    this.initFormatsCopies(signageItem, typeData.formatGroups);

    // Init default format (keep old format)
    const defaultFormat = getPosterService<ITemplatesService>(
      'templates'
    )?.getDefaultTemplateTypeFormat(typeData.formatGroups, signageItem.formatId ?? null);
    signageItem.formatId = defaultFormat?.itemId;

    // Init inputs summary
    signageItem.inputsSummary = this.getInputsSummaryFromValues(signageItem.values, {
      form: typeData.form!,
    });
  }

  /**
   * Builds a poster cart item.
   * Subsequent calls for the same type/family combo should complete in the same
   * order as called.
   *
   * @param familyId - id of the family for which to build
   * @param typeId - id of the type for which to build
   * @param userExtendedProperties - any extra values to add to the poster
   * @returns the cart poster
   */
  async buildCartPoster(
    familyId: string,
    typeId: string,
    userExtendedProperties: Record<string, any>
  ): Promise<CartPoster> {
    const poster = this.buildnewSignageItem(
      this.getNewSignageId(),
      familyId,
      userExtendedProperties
    );

    const [type, typeFormData, typeFormatGroups] = await Promise.all([
      getPosterService<ITemplatesService>('templates')!.loadType(typeId),
      getPosterService<ITemplatesService>('templates')!.loadTypeFormData(typeId),
      getPosterService<ITemplatesService>('templates')!.loadTypeFormatGroups(typeId),
    ]);
    (poster as any).type = type;

    // Init values with default values
    for (const alias in typeFormData.defaultValues) {
      if (
        typeFormData.defaultValues.hasOwnProperty(alias) &&
        typeof poster.values[alias] === 'undefined'
      ) {
        // Clone the value, so different posters won't reference the same
        // value
        const defaultValue = JSON.parse(JSON.stringify(typeFormData.defaultValues[alias]));

        const attribute = typeFormData.attributeMap.get(alias) ?? null;

        this.setPosterTaintedValue(poster, alias, false);
        this.setPosterValue(
          poster,
          alias,
          defaultValue,
          attribute,
          {
            attributeMap: typeFormData.attributeMap,
            valuesTypes: typeFormData.valuesTypes,
          },
          'replace'
        );
      }
    }

    // Init formats copies
    this.initFormatsCopies(poster, typeFormatGroups);

    // Init default format (keep old format)
    const defaultFormat = getPosterService<ITemplatesService>(
      'templates'
    )?.getDefaultTemplateTypeFormat(typeFormatGroups, null);
    (poster as any).formatId = defaultFormat?.itemId;

    // Init inputs summary
    (poster as any).inputsSummary = this.getInputsSummaryFromValues(poster.values, {
      form: typeFormData.form,
    });

    return (poster as unknown) as CartPoster;
  }

  /**
   * Updates the current format 'formatId' to be the first format with copies.
   * If no format has copies, doesn't change the format.
   *
   * @param poster - creating poster object
   */
  selectFirstFormatWithCopies(poster: CartPoster): void {
    // Find the first format that has copies, set the current format to be that format
    if (poster.formatsCopiesMap) {
      const firstFormatWithCopies = Object.keys(poster.formatsCopiesMap).find(
        formatId => poster.formatsCopiesMap[formatId].copies > 0
      );
      if (firstFormatWithCopies) {
        poster.formatId = firstFormatWithCopies;
      }
    }
  }

  /**
   *
   * @param {*} values
   * @param typeData - contains the form associated to the poster. Used to build the summary
   * @returns {Array} array of inputs summary generated from the signage values
   */
  getInputsSummaryFromValues(values: any, typeData: { form: TemplateForm.Form }): InputSummary[] {
    const inputsSummary: InputSummary[] = [];

    typeData.form?.inputGroups?.forEach(inputGroup => {
      for (const attribute of inputGroup.attributes) {
        if (attribute && attribute.options && attribute.options.visible) {
          inputsSummary.push({
            alias: attribute.alias,
            label: Vue.filter('piivoTranslate')(attribute),
            value: this.getInputSummaryValue(values, attribute.alias),
          });
        }
      }
    });

    return inputsSummary;
  }

  /**
   * Retrieves the human friendly value of an input summary from the signages values
   *
   * @param values - signage values
   * @param alias - the attribute alias, whose value to return
   * @returns the string representation of the value
   */
  getInputSummaryValue(values: Record<string, any>, alias: string): string {
    const value = values[alias];

    function formatArray(arr: unknown[]) {
      if (arr.some(el => typeof el === 'object' && el !== null)) {
        // Items are link item objects
        return arr.map(linkVal => Vue.filter('piivoTranslate')(linkVal)).join(', ');
      }
      // Items are simple primitives
      return arr.join(', ');
    }

    if (Array.isArray(value)) {
      if (value.length && Array.isArray(value[0])) {
        // Nested link
        return value.flatMap(el => formatArray(el)).join(', ');
      }
      // Simple link
      return formatArray(value);
    }

    return value;
  }

  /**
   * Update poster inputs summary.
   * @param poster - The poster to update
   */
  updateInputsSummary(poster: {
    values: { [aliasOrItemId: string]: unknown };
    inputsSummary: InputSummary[];
  }): void {
    // Update each input summary with signage item value
    for (const inputSummary of poster.inputsSummary) {
      inputSummary.value = this.getInputSummaryValue(poster.values, inputSummary.alias);
    }
  }

  /**
   * Initialize the signage item formats copies Object.
   *
   * @param signageItem - The signage item to initialize
   * @param formatGroups - the poster's type's format groups. Defines the formats
   * to reset to
   */
  initFormatsCopies(
    signageItem: { formatsCopiesMap: FormatsCopiesMap },
    formatGroups: TemplateFormats.FormatGroups[]
  ): void {
    const newformatsCopiesMap: FormatsCopiesMap = {};
    for (const formatGroup of formatGroups) {
      for (const format of formatGroup.formats) {
        newformatsCopiesMap[format.itemId] = {
          label: { ...format.label },
          copies: signageItem.formatsCopiesMap[format.itemId]?.copies || 0,
        };
      }
    }

    signageItem.formatsCopiesMap = newformatsCopiesMap;
  }

  /**
   * Check if formats copies object contains a valid number of copies for
   * the given format
   *
   * @param formatsCopiesMap - formats copies object
   * @param formatId - Format Id (Guid)
   * @returns the format copies validity
   */
  checkFormatCopies(
    formatsCopiesMap: Record<string, { copies?: number | string }>,
    formatId: string
  ): boolean {
    if (formatsCopiesMap != null) {
      const currentFormatCopies = formatsCopiesMap[formatId];
      return (
        formatsCopiesMap.hasOwnProperty(formatId) &&
        !!currentFormatCopies &&
        !!currentFormatCopies.copies &&
        Number.isInteger(Number(currentFormatCopies.copies)) &&
        currentFormatCopies.copies > 0
      );
    }

    return false;
  }

  /**
   * Creates a new formatsCopiesMap from the format info
   *
   * @param formatGroups - the groups of expected formats
   * @param formats - the format info map
   */
  formatInfoToCopiesMap(
    formatGroups: TemplateFormats.FormatGroups[],
    formats: { [formatAlias: string]: TemplateValues.FormatInfo }
  ): FormatsCopiesMap {
    const newformatsCopiesMap: FormatsCopiesMap = {};

    for (const formatGroup of formatGroups) {
      for (const format of formatGroup.formats) {
        newformatsCopiesMap[format.itemId] = {
          label: { ...format.label },
          copies: formats[format.alias]?.copies || 0,
        };
      }
    }

    return newformatsCopiesMap;
  }

  /**
   * Patch to add thumbnail in new logs created
   * @param {Array} signages - array contains object with signages id and thumbnail value
   */
  async patchLogsWithThumbnails(signages: any) {
    const res: any = await logs.patchLogsWithThumbnails(signages);
    return res.body;
  }

  /**
   * @returns the last used signage id
   */
  getCurrentSignageIdCounter(): number {
    return signageId;
  }

  /**
   * @returns a unique id for a signage in the cart
   */
  getNewSignageId(): number {
    return ++signageId;
  }

  /**
   * @param id - the id to rebase the counter upon
   * Will be ignored if the current counter is already bigger
   */
  rebaseSignageIdCounter(id: number): void {
    if (id > signageId) {
      signageId = id;
    }
  }

  /**
   * @param svgEl - the svg element from the preview component
   * @param template - the template that the svg is rendered from
   * @returns a normal svg as a string
   */
  generateSvgFromPreview(svgEl: Element, template: any): string | null {
    if (!svgEl) {
      return null;
    }

    // Get page width and height
    let pageWidth = 0;
    let pageHeight = 0;
    if (
      template &&
      template.computedTemplate &&
      template.computedTemplate.pages.length > 0 &&
      template.computedTemplate.pages[0] != null
    ) {
      pageWidth = template.computedTemplate.pages[0].width;
      pageHeight = template.computedTemplate.pages[0].height;
    }

    // Build SVG string

    const svgStr = new XMLSerializer().serializeToString(svgEl);

    return `<svg
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xmlns:svg="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      viewBox="0 0 ${pageWidth} ${pageHeight}"
    >
      ${svgStr}
    </svg>`;
  }

  /**
   * Reactively merges the values into the poster's values
   *
   * @param poster - The poster on which to set the value
   * @param values - The values to merge
   * @param typeData - corresponding typeData object of the poster
   */
  mergePosterValues(
    poster: { values: { [aliasOrItemId: string]: unknown } },
    values: { [aliasOrItemId: string]: unknown },
    typeData: TypeData | null
  ): void {
    for (const alias in values) {
      const value = values[alias];
      this.setPosterValue(poster, alias, value, null, typeData, 'replace');
    }
  }

  /**
   * Reactively sets a value on the poster
   *
   * @param poster - The poster on which to set the value
   * @param alias - Object key (property name)
   * @param value - New value
   * @param attribute - the attribute for the alias
   * @param typeData - corresponding typeData object of the poster
   */
  setPosterValue(
    poster: { values: { [aliasOrItemId: string]: unknown } },
    alias: string,
    value: unknown,
    attribute: TemplateForm.Attribute | null,
    typeData: Pick<TypeFormData, 'attributeMap' | 'valuesTypes'> | null,
    updateMode: SearchAttributeMapping['updateMode']
  ): void {
    const currentValue = poster.values[alias];
    const nextNormalizedValue = getNormalizedValue(
      value,
      (attribute || (typeData ? typeData.attributeMap.get(alias) : null)) ?? null,
      typeData ? typeData.valuesTypes[alias] : null
    );

    switch (updateMode) {
      case 'replace': {
        Vue.set(poster.values, alias, nextNormalizedValue);
        break;
      }
      case 'prepend': {
        Vue.set(poster.values, alias, [
          ...nextNormalizedValue,
          ...((currentValue ?? []) as unknown[]),
        ]);
        break;
      }
      case 'append': {
        Vue.set(poster.values, alias, [
          ...((currentValue ?? []) as unknown[]),
          ...nextNormalizedValue,
        ]);
        break;
      }
      default: {
        if (process.env.NODE_ENV === 'development') {
          console.error(`setPosterValue(): unknown updateMode '${updateMode}'`);
        }
        break;
      }
    }
  }

  /**
   * Applies the attribute mappings of the search mode to the search results
   * to create the new values object which to apply to the form
   *
   * @param searchResultItems - the search result items to apply to the form
   * @param searchMode - the search mode that provided the results
   * @param multipleResults if multiple results should be used, or only the first result
   * @returns a values object with the new attribute values to apply
   */
  mapSearchResultsToValues(
    searchResultItems: { [alias: string]: AttributeValues.AttributeValue }[],
    searchMode: SearchModeSearchConfiguration,
    multipleResults: boolean
  ): SearchResultUpdate {
    const onAttributeResult = searchMode.onAttributeResult;
    if (!onAttributeResult) {
      return {};
    }

    // Parse the mappings from JSON strings, to simple strings/string arrays
    const parsedMappings = Object.fromEntries(
      Object.entries(onAttributeResult.attributeMappings).map(([alias, mapping]) => {
        const parsedMapping = {
          // If 'select' is an array, parse it from json
          select: /\[.+\]/.test(mapping.select)
            ? (JSON.parse(mapping.select) as string[])
            : mapping.select,
          output: mapping.output,
          updateMode: mapping.updateMode,
        };
        return [alias, parsedMapping];
      })
    );

    // Use all results or only the first result
    const normalizedResult = multipleResults ? searchResultItems : searchResultItems.slice(0, 1);
    // Create the values object that will contain the mapped attribute values
    const mappedAttributeValues: {
      [alias: string]: {
        value: AttributeValues.AttributeValue;
        output: string;
        updateMode: SearchAttributeMapping['updateMode'];
      };
    } = {};

    // Step 1: select values per attribute via "select"
    normalizedResult.forEach(resultLineObj => {
      const selectInResultLine = (expression: string) => {
        // Handle eval in a future version
        if (expression === '') {
          return null;
        }
        return resultLineObj[expression];
      };

      Object.entries(parsedMappings).forEach(([alias, mapping]) => {
        let mappedLineValue: AttributeValues.AttributeValue | null = null;

        if (Array.isArray(mapping.select)) {
          // Array: the mapped line value is the array of evaluated select expressions
          // in the same order
          mappedLineValue = mapping.select.map(selectCol =>
            selectInResultLine(selectCol)
          ) as AttributeValues.AttributeValue;
          // Cast as a simple attribute value, since an array of attribute values
          // is itself a simple attribute value: links combo, links grid...
        } else {
          // Else the mapped line value is simply the evaluated select expression
          mappedLineValue = selectInResultLine(mapping.select);
        }

        if (multipleResults) {
          // If multiple lines (search results) should be used, each mapped attribute value
          // is simply the array of all the mapped line values

          mappedAttributeValues[alias] = mappedAttributeValues[alias] ?? {
            value: [],
            output: mapping.output,
            updateMode: mapping.updateMode ?? 'replace',
          };
          // Add the current mapped line value to the final attribute value
          (mappedAttributeValues[alias].value as Array<unknown>).push(mappedLineValue);
        } else {
          // Else each mapped attribute value is the first mapped line value
          mappedAttributeValues[alias] = {
            value: mappedLineValue,
            output: mapping.output,
            updateMode: mapping.updateMode ?? 'replace',
          };
        }
      });
    });

    // Step 2: format values per attribute via "output"
    // handle formatters in mapping.output in a future version
    Object.values(mappedAttributeValues).forEach(update => {
      if (update.output === 'TABLE') {
        // Ensure value is 2d array
        let value = update.value as unknown;
        if (!Array.isArray(value)) {
          value = [value];
        }
        if (!Array.isArray((value as Array<unknown>)[0])) {
          value = [value];
        }
        update.value = value as AttributeValues.AttributeValue;
      }
    });

    return mappedAttributeValues;
  }

  /**
   * Reactively merges the tainted values object flags
   * into the poster's tainted values object
   *
   * @param poster - The poster on which to set the values
   * @param taintedValues - The values to merge
   */
  mergePosterTaintedValues(
    poster: { taintedValues: { [aliasOrItemId: string]: boolean } },
    taintedValues: { [aliasOrItemId: string]: boolean }
  ): void {
    for (const alias in taintedValues) {
      this.setPosterTaintedValue(poster, alias, taintedValues[alias]);
    }
  }

  /**
   * Reactively sets a tainted value flag on the poster
   *
   * @param poster - The poster on which to set the value
   * @param alias - Object key (property name)
   * @param tainted - New value of the tainted flag
   */
  setPosterTaintedValue(
    poster: { taintedValues: { [aliasOrItemId: string]: boolean } },
    alias: string,
    tainted: boolean
  ): void {
    Vue.set(poster.taintedValues, alias, tainted);
  }
}
